import { Chart } from "chart.js";
import "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import * as moment from "moment";
import React, { useEffect, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { numericFormatter } from "react-number-format";
import { useLocation } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import { Block, BlockBetween, BlockHead, BlockHeadContent, BlockTitle, Col, Icon, Row, RSelect } from "../../components/Component";
import DatePicker from "../../components/range-date-picker/RangeDatePicker";
import { GET } from "../../helper/api";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { EXCHANGE_INTERNAL_INVENTORY_OPTIONS } from "../../utils/Utils";
import { getColorsArr } from "../../utils/color";

Chart.register(ChartDataLabels);

const IngredientReport = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [smOption, setSmOption] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState();
  const [rangeDate, setRangeDate] = useState({
    start: moment().startOf("months").toDate(),
    end: moment().endOf("months").toDate(),
  });
  const [warehouseOptions, setWarehouseOptions] = useState([]);

  const [filter, setFilter] = useState({
    warehouseId: searchParams.get("warehouseId") || undefined,
    isExchangeInternalInventory: searchParams.get("isExchangeInternalInventory") || undefined,
  });

  const onRangeChange = (dates) => {
    const [start, end] = dates;
    setRangeDate({ start: start ? moment(start)?.startOf("days").toDate() : start, end: end ? moment(end)?.endOf("days").toDate() : end });
    if (start && end) {
      setUpdatedAt(moment().valueOf());
    }
  };

  useEffect(() => {
    GET("/warehouse", {}, true).then((resp) => {
      setWarehouseOptions(
        resp.data?.rows?.map(
          (c) =>
          ({
            label: c.name,
            value: c.id,
            data: c,
          })
        )
      );
    });
  }, [])

  useEffect(() => {
    GET(
      `/report/report-ingredient-weight-by-timerange`,
      {
        from: moment(rangeDate.start).format("YYYY-MM-DD"),
        to: moment(rangeDate.end).format("YYYY-MM-DD"),
        ...(filter.warehouseId ? { warehouseId: filter.warehouseId } : {}),
        ...(filter.isExchangeInternalInventory !== undefined ? { isExchangeInternalInventory: filter.isExchangeInternalInventory } : {}),
      },
      true
    ).then(({ data: { data: tempData } }) => {
      const colors = getColorsArr(500);
      const data = tempData.filter((e) => ["VHD", "DTP", "DDT", "BHD"].includes(e.ingredientId));
      // chi tiền = nhập kho
      const tempChartData = {
        in: {
          labels: data.map((c) => c.name),
          datasets: [
            {
              data: data.map((c) => c.outWeight),
              backgroundColor: colors,
              hoverOffset: 4,
            },
          ],
        },
        inOption: {
          plugins: {
            datalabels: {
              color: "black",
              textStrokeColor: "white",
              textStrokeWidth: 3,
              formatter: (value, context) => {
                if (!value) {
                  return "";
                }
                return `${data[context.dataIndex].percentOutWeight} %`;
              },
            },
            tooltip: {
              callbacks: {
                label: (context) => `${context.formattedValue} kg`,
              },
            },
          },
        },
        inBarOption: {
          plugins: {
            legend: {
              display: false
            },
            datalabels: {
              color: "black",
              textStrokeColor: "white",
              textStrokeWidth: 3,
              formatter: (value, context) => {
                return numericFormatter(`${value}`, {
                  thousandsGroupStyle: "thousand",
                  thousandSeparator: ",",
                  suffix: " kg",
                  decimalScale: 2
                })
              },
            },
          },
        },
        out: {
          labels: data.map((c) => c.name),
          datasets: [
            {
              data: data.map((c) => c.inWeight),
              backgroundColor: colors,
              hoverOffset: 4,
            },
          ],
        },
        outOption: {
          plugins: {
            datalabels: {
              color: "black",
              textStrokeColor: "white",
              textStrokeWidth: 3,
              formatter: (value, context) => {
                if (!value) {
                  return "";
                }
                return `${data[context.dataIndex].percentInWeight} %`;
              },
            },
            tooltip: {
              callbacks: {
                label: (context) => `${context.formattedValue} kg`,
              },
            },
          },
        },
        outBarOption: {
          plugins: {
            legend: {
              display: false
            },
            datalabels: {
              color: "black",
              textStrokeColor: "white",
              textStrokeWidth: 3,
              formatter: (value, context) => {
                return numericFormatter(`${value}`, {
                  thousandsGroupStyle: "thousand",
                  thousandSeparator: ",",
                  suffix: " kg",
                  decimalScale: 2
                })
              },
            },
          },
        },
      };
      setChartData(tempChartData);
    });
  }, [updatedAt, filter]);

  return (
    <React.Fragment>
      <Head title="Báo cáo"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Báo cáo</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div className="toggle-expand-content" style={{ display: smOption ? "block" : "none" }}>
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <div className="form-group">
                        <div className="form-control-wrap">
                          <DatePicker
                            selected={rangeDate.start}
                            startDate={rangeDate.start}
                            onChange={onRangeChange}
                            endDate={rangeDate.end}
                            maxDate={new Date()}
                            selectsRange
                            className="form-control date-picker"
                          />
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="form-group">
                        <div className="form-control-wrap">
                          <RSelect
                            isClearable
                            placeholder={"Lọc theo kho"}
                            options={warehouseOptions}
                            value={warehouseOptions.find((c) => c.value === filter.warehouseId) || null}
                            onChange={(val) => {
                              setFilter({
                                ...filter,
                                warehouseId: val?.value || undefined,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </li>
                    <li>
                      <div className="form-group">
                        <div className="form-control-wrap">
                          <RSelect
                            isClearable
                            placeholder={"Lọc loại"}
                            options={EXCHANGE_INTERNAL_INVENTORY_OPTIONS}
                            value={EXCHANGE_INTERNAL_INVENTORY_OPTIONS.find((c) => c.value === filter.isExchangeInternalInventory) || null}
                            onChange={(val) => {
                              setFilter({
                                ...filter,
                                isExchangeInternalInventory: val?.value,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </li>

                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>

          {chartData ? (
            <Card>
              <CardBody>
                <Row>
                  <Col sm={12} md={3}>
                    <h3>Nhập kho </h3>
                    <Doughnut data={chartData?.in} options={chartData.inOption} />

                    <br />
                    <Bar data={chartData?.in} options={chartData.inBarOption} />
                  </Col>
                  <Col sm={12} md={3}>
                    <h3>Xuất kho</h3>
                    <Doughnut data={chartData?.out} options={chartData.outOption} />
                    <br />
                    <Bar data={chartData?.out} options={chartData.outBarOption} />

                  </Col>
                </Row>
              </CardBody>
            </Card>
          ) : (
            ""
          )}
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default IngredientReport;
